import React from 'react';

import { Flex, Group, Popover, Text } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';

import { formatUtcDate } from '@/utils/formatDate';
import InfoIcon from '@/assets/icons/InfoIcon';

import { PatientActiveStatusEnum } from '@/types/patient';

import styles from './cells.module.scss';

interface StatusCellProps {
  status: string;
  statusReason: string;
  statusOtherReason: string;
  statusStartDate?: string | Date | null;
  statusEndDate?: string | Date | null;
  statusUpdatedAt?: string | Date | null;
}

const PatientStatusPopover = ({
  label,
  reason,
  pauseDates
}: {
  label?: string;
  reason?: string;
  pauseDates?: { start?: string | Date | null; end?: string | Date | null };
}) => {
  const [opened, { close, open }] = useDisclosure(false);

  return (
    <Popover
      styles={{ dropdown: { maxWidth: '250px', minWidth: '200px' } }}
      opened={opened}
      position="bottom-start"
      withArrow
      arrowPosition="side"
      arrowOffset={14}
      arrowSize={8}
      arrowRadius={1}
      offset={{ mainAxis: 4, crossAxis: 0 }}>
      <Popover.Target>
        <Flex onMouseEnter={open} onMouseLeave={close} align={'center'}>
          <InfoIcon />
        </Flex>
      </Popover.Target>
      <Popover.Dropdown bg={'plum'} p={'xs'} style={{ borderRadius: '8px ' }}>
        {/*<Text fw={600}>Status when happened</Text>*/}
        {pauseDates?.start && pauseDates?.end && (
          <Text fz={'xs'} c={'white'}>
            Pause Date:{' '}
            <span
              style={{
                display: 'inline'
              }}>{`${formatUtcDate(pauseDates.start)} until ${formatUtcDate(pauseDates.end)}`}</span>
          </Text>
        )}
        <Text fz={'xs'} c={'white'}>
          {label}:{' '}
          <span style={{ display: 'inline' }}>{reason && reason.trim() != '' ? reason : '-'}</span>
        </Text>
      </Popover.Dropdown>
    </Popover>
  );
};

const getPatientStatus = (status?: string) => {
  if (!status || !['Paused', 'Inactive'].includes(status)) return PatientActiveStatusEnum.Active;
  else if (status === 'Paused') return PatientActiveStatusEnum.Paused;
  return PatientActiveStatusEnum.Inactive;
};

export const StatusCell: React.FC<StatusCellProps> = ({
  status,
  statusReason,
  statusOtherReason,
  statusStartDate,
  statusEndDate,
  statusUpdatedAt
}) => (
  <Group gap={4} align="center">
    <Text className={styles.statusText}>{getPatientStatus(status) || 'No Status'}</Text>
    {getPatientStatus(status) === PatientActiveStatusEnum.Active && (
      <PatientStatusPopover
        label="Active"
        reason={statusUpdatedAt ? formatUtcDate(statusUpdatedAt) : '-'}
      />
    )}
    {getPatientStatus(status) === PatientActiveStatusEnum.Inactive && (
      <PatientStatusPopover label="Reason" reason={statusReason ? statusReason : '-'} />
    )}
    {getPatientStatus(status) === PatientActiveStatusEnum.Paused && (
      <PatientStatusPopover
        label="Reason"
        reason={statusReason ? statusReason : statusOtherReason}
        pauseDates={{ start: statusStartDate, end: statusEndDate }}
      />
    )}
  </Group>
);
